import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../portal/primitives';
import { useT } from '../lib/i18n';

export interface Command {
  to: string;
  label: string;
  /** Section/parent shown as a muted suffix (e.g. "Sistema", "Definições"). */
  section?: string;
  icon?: string;
  /** Extra words that should match the search (synonyms, abbreviations). */
  keywords?: string;
}

/** Accent/case-insensitive normalization for matching. */
function norm(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * A ⌘K / Ctrl+K command palette: a searchable launcher to jump to any module
 * or page the user can reach. It complements the sidebar (which stays for
 * discovery) rather than replacing it. Fully keyboard-driven: ↑/↓ to move,
 * Enter to open, Esc to close.
 */
export function CommandPalette({
  open,
  onClose,
  commands,
}: {
  open: boolean;
  onClose: () => void;
  commands: Command[];
}) {
  const navigate = useNavigate();
  const t = useT();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Reset the query and selection each time the palette opens, and focus it.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      // Focus after the element is in the DOM.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const results = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return commands;
    const terms = q.split(/\s+/);
    return commands.filter((c) => {
      const hay = norm(`${c.label} ${c.section ?? ''} ${c.keywords ?? ''}`);
      return terms.every((t) => hay.includes(t));
    });
  }, [query, commands]);

  // Keep the active index in range as results change.
  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, results.length - 1)));
  }, [results.length]);

  // Keep the highlighted row scrolled into view.
  useLayoutEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  if (!open) return null;

  function choose(cmd: Command | undefined) {
    if (!cmd) return;
    onClose();
    navigate(cmd.to);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(results[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <div className="cmdk-scrim" onMouseDown={onClose}>
      <div
        className="cmdk"
        role="dialog"
        aria-label={t('cmd.aria')}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cmdk-search">
          <Icon name="search" size={18} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('cmd.placeholder')}
            aria-label={t('cmd.aria')}
          />
          <kbd className="cmdk-esc">esc</kbd>
        </div>
        <div className="cmdk-list" ref={listRef}>
          {results.length === 0 ? (
            <div className="cmdk-empty muted">{t('cmd.empty')}</div>
          ) : (
            results.map((c, i) => (
              <button
                key={c.to}
                type="button"
                data-idx={i}
                className={`cmdk-item${i === active ? ' on' : ''}`}
                onMouseMove={() => setActive(i)}
                onClick={() => choose(c)}
              >
                {c.icon && <Icon name={c.icon} size={17} className="cmdk-ic" />}
                <span className="cmdk-label">{c.label}</span>
                {c.section && <span className="cmdk-section">{c.section}</span>}
              </button>
            ))
          )}
        </div>
        <div className="cmdk-foot">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> {t('cmd.hintNavigate')}
          </span>
          <span>
            <kbd>↵</kbd> {t('cmd.hintOpen')}
          </span>
          <span>
            <kbd>esc</kbd> {t('cmd.hintClose')}
          </span>
        </div>
      </div>
    </div>
  );
}
